'use client';

/**
 * CommandPalettePreview Component
 *
 * Entity preview panel for the enhanced Command Palette.
 * Displays entity details when a search result is focused.
 *
 * @module components/CommandPalette/CommandPalettePreview
 * @see Issue #236 - Cross-Entity Global Search
 */

import DocumentIcon from '@mui/icons-material/Article';
import BusinessIcon from '@mui/icons-material/Business';
import CaseIcon from '@mui/icons-material/Folder';
import LeadIcon from '@mui/icons-material/Leaderboard';
import ViewIcon from '@mui/icons-material/OpenInNew';
import ConsumerIcon from '@mui/icons-material/Person';
import UserIcon from '@mui/icons-material/PersonOutlined';
import { alpha, Box, Button, Chip, Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { Easing } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { useI18n } from '../../hooks/useI18n';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { iconSizes } from '../../theme/typography';
import { AiIcon } from '../Icons';
import { type PanelAttribute, PanelAttributeList } from '../SplitPanel/PanelAttributeList';
import type { CommandPalettePreviewProps } from './types';

// ============================================================================
// Styled Components
// ============================================================================

const PreviewContainer = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.01),
  overflow: 'hidden',
}));

const PreviewHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const EntityIconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 8,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  flexShrink: 0,
}));

const PreviewContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
}));

const PreviewFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : alpha(theme.palette.common.black, 0.02),
}));

const StatusChip = styled(Chip)<{ $statusColor: string }>(({ theme, $statusColor }) => ({
  height: 22,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: alpha(
    $statusColor === 'success'
      ? theme.palette.success.main
      : $statusColor === 'warning'
        ? theme.palette.warning.main
        : $statusColor === 'error'
          ? theme.palette.error.main
          : $statusColor === 'info'
            ? theme.palette.info.main
            : theme.palette.grey[500],
    0.15,
  ),
  color:
    $statusColor === 'success'
      ? theme.palette.success.main
      : $statusColor === 'warning'
        ? theme.palette.warning.main
        : $statusColor === 'error'
          ? theme.palette.error.main
          : $statusColor === 'info'
            ? theme.palette.info.main
            : theme.palette.grey[500],
  border: `1px solid ${alpha(
    $statusColor === 'success'
      ? theme.palette.success.main
      : $statusColor === 'warning'
        ? theme.palette.warning.main
        : $statusColor === 'error'
          ? theme.palette.error.main
          : $statusColor === 'info'
            ? theme.palette.info.main
            : theme.palette.grey[500],
    0.3,
  )}`,
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: theme.spacing(3),
  color: theme.palette.text.secondary,
}));

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the icon for an entity type
 */
function getEntityIcon(entityType: string): React.ReactNode {
  switch (entityType) {
    case 'case':
      return <CaseIcon />;
    case 'consumer':
      return <ConsumerIcon />;
    case 'user':
      return <UserIcon />;
    case 'business':
      return <BusinessIcon />;
    case 'lead':
      return <LeadIcon />;
    case 'document':
      return <DocumentIcon />;
    default:
      return <CaseIcon />;
  }
}

// ============================================================================
// Animation Variants
// ============================================================================

// Easing curve for animations
const easeOutCubic: Easing = [0.215, 0.61, 0.355, 1];

const containerVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.2,
      ease: easeOutCubic,
    },
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: {
      duration: 0.15,
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export const CommandPalettePreview: React.FC<CommandPalettePreviewProps> = ({
  data,
  isLoading = false,
  width = 280,
  onQuickAction,
  onStartChat,
  onViewDetails,
}) => {
  const { t } = useI18n();
  const reducedMotion = useReducedMotion();

  // Transform preview attributes to PanelAttribute format
  const attributes: PanelAttribute[] = React.useMemo(() => {
    if (!data?.attributes) return [];
    return data.attributes.map((attr) => ({
      label: attr.label,
      value: attr.value,
      icon: attr.icon,
      copyable: attr.copyable,
    }));
  }, [data?.attributes]);

  // Loading state
  if (isLoading) {
    return (
      <PreviewContainer style={{ width }} initial={false} animate={{ opacity: 1 }}>
        <PreviewHeader>
          <Skeleton variant="rounded" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="50%" height={20} />
          </Box>
        </PreviewHeader>
        <PreviewContent>
          <PanelAttributeList attributes={[]} loading skeletonCount={4} compact />
        </PreviewContent>
        <PreviewFooter>
          <Skeleton variant="rounded" width={100} height={32} />
          <Skeleton variant="rounded" width={80} height={32} />
        </PreviewFooter>
      </PreviewContainer>
    );
  }

  // Empty state
  if (!data) {
    return (
      <PreviewContainer style={{ width }} initial={false} animate={{ opacity: 1 }}>
        <EmptyState>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            {t('palette.preview.noPreview')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mt: 1,
              textAlign: 'center',
            }}
          >
            {t('palette.preview.selectHint')}
          </Typography>
        </EmptyState>
      </PreviewContainer>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <PreviewContainer
        key={data.id}
        style={{ width }}
        variants={reducedMotion ? undefined : containerVariants}
        initial={reducedMotion ? false : 'hidden'}
        animate="visible"
        exit={reducedMotion ? undefined : 'exit'}
      >
        {/* Header */}
        <PreviewHeader>
          <EntityIconContainer>{getEntityIcon(data.entityType)}</EntityIconContainer>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {data.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'capitalize',
                }}
              >
                {data.entityType}
              </Typography>
              {data.status && <StatusChip label={data.status.label} size="small" $statusColor={data.status.color} />}
            </Box>
          </Box>
        </PreviewHeader>

        {/* Content */}
        <PreviewContent>
          <PanelAttributeList attributes={attributes} compact dividers={false} />

          {/* Last Updated */}
          {data.lastUpdated && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 2,
                textAlign: 'right',
              }}
            >
              {t('palette.preview.lastUpdated', {
                time: new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
                  Math.round((data.lastUpdated.getTime() - Date.now()) / (1000 * 60 * 60)),
                  'hour',
                ),
              })}
            </Typography>
          )}
        </PreviewContent>

        {/* Footer */}
        <PreviewFooter>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewIcon sx={{ fontSize: iconSizes.sm }} />}
            onClick={() => onViewDetails?.(data)}
            sx={{ flex: 1, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            {t('palette.preview.viewDetails')}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AiIcon sx={{ fontSize: iconSizes.sm }} />}
            onClick={() => onStartChat?.(data)}
            sx={{ flex: 1, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            {t('palette.preview.askAi')}
          </Button>
        </PreviewFooter>

        {/* Quick Actions */}
        {data.quickActions && data.quickActions.length > 0 && (
          <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {data.quickActions.map((action) => (
              <Button
                key={action.id}
                size="small"
                variant="text"
                startIcon={action.icon}
                disabled={action.disabled}
                onClick={() => {
                  action.onClick();
                  onQuickAction?.(action.id);
                }}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </PreviewContainer>
    </AnimatePresence>
  );
};

CommandPalettePreview.displayName = 'CommandPalettePreview';

export default CommandPalettePreview;
